import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { getPressArticleBySlug } from '@/content/news/articles'
import { useEffect } from 'react'

const ARTICLE_SLUG = 'bloomberg-searching-for-work'

export function BloombergSearchingForWorkPage() {
  const article = getPressArticleBySlug(ARTICLE_SLUG)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (!article) return
    const previousTitle = document.title
    document.title = `${article.headline} | Pelgo`
    return () => {
      document.title = previousTitle
    }
  }, [article])

  if (!article) {
    return null
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MarketingNav variant="standalone" />

      <article className="flex-1 pt-32">
        <div
          className="py-16 md:py-24"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
          }}
        >
          <div className="container mx-auto px-4 md:px-16">
            <div className="max-w-[1280px] mx-auto">
              <header className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wide text-pelgo-blue-light mb-4">
                  {article.eyebrow}
                </p>
                <div className="h-3 w-12 bg-pelgo-blue-light rounded-full mb-6" aria-hidden />
                <h1 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                  {article.headline}
                </h1>
                <p className="mt-6 text-base font-medium text-gray-500 tracking-[-0.32px] leading-relaxed">
                  {article.dek}
                </p>
                <p className="mt-6 text-sm text-gray-600">
                  <span className="font-medium text-gray-900">{article.byline}</span>
                  <span className="mx-2 text-gray-400" aria-hidden>
                    |
                  </span>
                  <time dateTime="2026-04-17">{article.dateline}</time>
                </p>
              </header>

              <figure className="mt-10 md:mt-12 max-w-4xl">
                <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)] aspect-[3/2] bg-gray-100">
                  <img
                    src={article.heroImageSrc}
                    alt={article.heroImageAlt}
                    className="h-full w-full object-cover"
                    width={1200}
                    height={800}
                    loading="eager"
                    decoding="async"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-gray-500">
                  Photo accompanying the original {article.publicationName} piece.
                </figcaption>
              </figure>

              <div className="mt-10 md:mt-12 max-w-3xl">
                {article.newsletterIntro ? (
                  <p className="text-base text-gray-600 leading-relaxed border-l-2 border-gray-200 pl-5 italic">
                    {article.newsletterIntro}
                  </p>
                ) : null}

                <h2 className="font-display text-2xl md:text-3xl text-gray-900 mt-10 md:mt-14 mb-6 tracking-tight">
                  {article.sectionHeading}
                </h2>

                <div className="prose prose-slate max-w-none prose-p:text-gray-700 prose-p:leading-relaxed prose-p:tracking-[-0.01em]">
                  {article.bodyParagraphs.map((paragraph) => (
                    <p key={paragraph} className="text-slate-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <aside
                  className="mt-12 md:mt-16 rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8"
                  aria-label="Source"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                    <img
                      src="/assets/logos/bloomberg.png"
                      alt=""
                      width={140}
                      height={32}
                      className="h-8 w-auto object-contain object-left flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1 space-y-3 text-sm text-gray-700 leading-relaxed">
                      <p className="font-medium text-gray-900">
                        {article.publicationName} — {article.publicationSeries}
                      </p>
                      <p>
                        Excerpt from the newsletter dated {article.sourcePublishedLabel}. The full
                        article may require a Bloomberg subscription.
                      </p>
                      <p>
                        <a
                          href={article.sourceUrl}
                          className="text-pelgo-blue-light font-medium underline-offset-2 hover:underline break-words"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {article.sourceUrl}
                        </a>
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </article>

      <SiteFooter />
    </div>
  )
}
